import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const SearchablePersonSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = "Search by name...",
  excludeId = null 
}) => {
  const { theme, isDark } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const containerRef = useRef(null);

  // Filter options based on search term and exclude current person
  const filteredOptions = options
    .filter(person => {
      if (!person || !person.id) return false;
      if (excludeId && person.id === excludeId) return false;
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const firstName = (person.preferred_name || person.first_name || '').toLowerCase();
      const lastName = (person.last_name || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName.includes(searchLower) || firstName.includes(searchLower) || lastName.includes(searchLower);
    })
    .slice(0, 10); // Limit to 10 results

  // Set selected person when value changes
  useEffect(() => {
    if (value) {
      const person = options.find(p => p.id === value);
      if (person) {
        setSelectedPerson(person);
        setSearchTerm(`${person.preferred_name || person.first_name} ${person.last_name || ''}`.trim());
      }
    } else {
      setSelectedPerson(null);
      setSearchTerm('');
    }
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (person) => {
    setSelectedPerson(person);
    setSearchTerm(`${person.preferred_name || person.first_name} ${person.last_name || ''}`.trim());
    onChange(person.id);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true); // Always show dropdown when typing
    if (!term) {
      setSelectedPerson(null);
      onChange('');
    }
  };
  
  const handleFocus = () => {
    setIsOpen(true); // Show dropdown when input is focused
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '6px',
          border: theme.glass.border,
          background: theme.card.background,
          color: theme.text.primary,
          fontSize: '16px',
          minHeight: '44px',
        }}
      />
      {isOpen && options && options.length > 0 && filteredOptions && filteredOptions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10000,
            maxHeight: '300px',
            overflowY: 'auto',
            background: isDark ? 'rgba(30, 30, 40, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
            borderRadius: '6px',
            marginTop: '4px',
            boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.2)',
          }}
          className="searchable-person-dropdown"
        >
          {filteredOptions.map((person) => {
            const personColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.15)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.15)' : 'rgba(107, 114, 128, 0.15)';
            const hoverColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.25)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.25)' : 'rgba(107, 114, 128, 0.25)';
            return (
              <div
                key={person.id}
                onClick={() => handleSelect(person)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  background: person.id === value ? personColor : (isDark ? 'rgba(30, 30, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)'),
                  borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '44px',
                  touchAction: 'manipulation',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = hoverColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = person.id === value ? personColor : (isDark ? 'rgba(30, 30, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)');
                }}
              >
                {person.picture_url && (
                  <img
                    src={person.picture_url}
                    alt={person.preferred_name || person.first_name}
                    style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                  />
                )}
                <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                  {person.sex === 'male' ? '♂' : person.sex === 'female' ? '♀' : '⚥'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: theme.text.primary }}>
                    {person.preferred_name || person.first_name} {person.last_name || ''}
                  </div>
                  {person.date_of_birth && (
                    <div style={{ fontSize: '12px', color: theme.text.secondary }}>
                      {new Date(person.date_of_birth).getFullYear()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {isOpen && searchTerm && filteredOptions.length === 0 && options.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10000,
            padding: '12px',
            background: isDark ? 'rgba(30, 30, 40, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
            borderRadius: '6px',
            marginTop: '4px',
            boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.2)',
            color: theme.text.secondary,
            fontSize: '14px',
          }}
          className="searchable-person-dropdown"
        >
          No matches found
        </div>
      )}
      {isOpen && options.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10000,
            padding: '12px',
            background: isDark ? 'rgba(30, 30, 40, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
            borderRadius: '6px',
            marginTop: '4px',
            boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.2)',
            color: theme.text.secondary,
            fontSize: '14px',
          }}
          className="searchable-person-dropdown"
        >
          No people available. Please ensure you have family members added.
        </div>
      )}
    </div>
  );
};

export default SearchablePersonSelect;



